<template>
  <div class="page-enter">
    <div class="page-head"><div><h2>对象存储设置</h2><p>管理火山 TOS 存储总开关、默认空间和会员单独空间额度。</p></div><el-button icon="el-icon-refresh" :loading="loading || usageLoading" @click="load">刷新</el-button></div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon class="section-alert" />
    <el-card class="profile-page">
      <div slot="header"><strong>全局存储策略</strong></div>
      <el-form ref="form" :model="form" :rules="rules" label-position="top" :disabled="saving || loading">
        <el-form-item label="启用对象存储"><el-switch v-model="form.enabled" active-text="允许会员上传" inactive-text="关闭上传" /></el-form-item>
        <el-form-item label="每位会员默认空间（GB）" prop="default_quota_gb"><el-input-number v-model="form.default_quota_gb" :min="0" :max="1000000" :step="1" :precision="3" controls-position="right" /><p class="form-hint">按 1GB = 1024³ 字节计算；会员没有单独额度时使用此值。</p></el-form-item>
        <div class="drawer-actions"><el-button type="primary" :loading="saving" :disabled="isDemo" @click="save">保存设置</el-button></div>
      </el-form>
    </el-card>
    <el-card>
      <div slot="header"><strong>会员空间用量</strong><span class="muted small" style="float:right">默认空间：{{ Number(form.default_quota_gb).toLocaleString() }} GB</span></div>
      <el-table :data="usageRows" v-loading="usageLoading" empty-text="暂无会员用量">
        <el-table-column prop="username" label="会员账号" min-width="150" />
        <el-table-column prop="display_name" label="显示名" min-width="140" />
        <el-table-column label="已用空间" min-width="120"><template slot-scope="s">{{ Number(s.row.used_gb || 0).toFixed(3) }} GB</template></el-table-column>
        <el-table-column label="单独额度" min-width="120"><template slot-scope="s"><span v-if="s.row.quota_override_gb !== null && s.row.quota_override_gb !== undefined">{{ Number(s.row.quota_override_gb).toLocaleString() }} GB</span><span v-else class="muted">跟随默认</span></template></el-table-column>
        <el-table-column label="生效额度" min-width="120"><template slot-scope="s">{{ Number(s.row.effective_quota_gb || 0).toLocaleString() }} GB</template></el-table-column>
        <el-table-column label="剩余空间" min-width="120"><template slot-scope="s">{{ Number(s.row.remaining_gb || 0).toFixed(3) }} GB</template></el-table-column>
        <el-table-column label="操作" width="130"><template slot-scope="s"><el-button type="text" size="mini" :disabled="isDemo || saving" @click="editQuota(s.row)">设置</el-button><el-button v-if="s.row.quota_override_gb !== null && s.row.quota_override_gb !== undefined" type="text" size="mini" class="danger-text" :disabled="isDemo || saving" @click="clearQuota(s.row)">清除</el-button></template></el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '../../api';
import { errorMessage } from '../../utils/management.cjs';
export default {
  name: 'StorageSettings',
  data: () => ({
    form: { enabled: false, default_quota_gb: 5 }, usageRows: [], loading: false, usageLoading: false, saving: false, error: '',
    rules: { default_quota_gb: [{ required: true, message: '请输入默认空间', trigger: 'change' }, { validator: (rule, value, done) => (Number.isFinite(value) && value >= 0 ? done() : done(new Error('空间必须是大于等于0的数字'))) }] }
  }),
  computed: { isDemo() { return this.$store.getters.isDemo; } },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.usageLoading = true; this.error = '';
      try {
        const settings = await api.storageSettings();
        this.form = { enabled: !!Number(settings.enabled), default_quota_gb: Number(settings.default_quota_gb || 0) };
        this.usageRows = await api.storageUsage();
      } catch (error) { this.error = errorMessage(error, '对象存储设置加载失败，请点击刷新重试'); }
      finally { this.loading = false; this.usageLoading = false; }
    },
    async save() {
      try {
        await this.$refs.form.validate();
        if (this.isDemo) return;
        this.saving = true; this.error = '';
        const saved = await api.updateStorageSettings({ enabled: !!this.form.enabled, default_quota_gb: Number(this.form.default_quota_gb) });
        this.form = { enabled: !!Number(saved.enabled), default_quota_gb: Number(saved.default_quota_gb || 0) };
        await this.load();
        this.$message.success('对象存储设置已保存');
      } catch (error) { if (error !== false) this.error = errorMessage(error, '对象存储设置保存失败'); }
      finally { this.saving = false; }
    },
    async editQuota(row) {
      try {
        const result = await this.$prompt('请输入会员单独空间（GB）', '设置会员空间：' + (row.display_name || row.username), { inputValue: row.quota_override_gb == null ? '' : String(row.quota_override_gb), inputPattern: /^\\d+(\\.\\d{1,3})?$/, inputErrorMessage: '请输入最多3位小数的非负数字' });
        await this.saveQuota(row, Number(result.value));
      } catch (error) { if (error !== 'cancel') this.error = errorMessage(error, '会员空间保存失败'); }
    },
    async clearQuota(row) {
      try {
        await this.$confirm('确定清除 ' + (row.display_name || row.username) + ' 的单独空间吗？清除后将恢复默认空间。', '清除会员空间', { type: 'warning' });
        await this.saveQuota(row, null);
      } catch (error) { if (error !== 'cancel') this.error = errorMessage(error, '会员空间清除失败'); }
    },
    async saveQuota(row, quota) {
      this.saving = true; this.error = '';
      try { await api.updateStorageQuota(Number(row.user_id), quota); await this.load(); this.$message.success('会员空间已更新'); }
      catch (error) { this.error = errorMessage(error, '会员空间保存失败'); }
      finally { this.saving = false; }
    }
  }
};
</script>

